/**
 * OfferEdit Page
 * 
 * Edit form for existing offers with context editor.
 * Matches real database schema (no status or date fields).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { logger } from '@/app/utils/logger';
import { Card, CardHeader, CardBody } from '@/components/common/Card';
import { Input, Select } from '@/components/common/FormField';
import { OfferContextEditor } from '@/components/offers/OfferContextEditor';
import { PriorityValidation } from '@/components/offers/PriorityValidation';
import { offerService } from '@/app/services/offerService';
import { auditService } from '@/app/services';
import { useOfferData } from '@/app/hooks/useOfferData';
import type { OfferResponse, UpdateOfferRequest, OfferContext, AssignmentStrategy } from '@/app/types/offers';
import { ASSIGNMENT_STRATEGY_OPTIONS } from '@/app/types/offers';
import './OfferEdit.scss';

export const OfferEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { invalidate, refresh } = useOfferData();

  const [originalOffer, setOriginalOffer] = useState<OfferResponse | null>(null);
  const [formData, setFormData] = useState<UpdateOfferRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchOffer = async () => {
      if (!id) {
        setError('Offer ID is missing');
        setLoading(false);
        return;
      }

      try {
        const data = await offerService.getOfferById(id);
        if (data) {
          setOriginalOffer(data);
          setFormData({
            assignment_strategy: data.assignment_strategy,
            context_json: data.context_json,
            display_priority: data.display_priority,
          });
        } else {
          setError('Offer not found');
        }
      } catch (err) {
        setError('Failed to load offer');
        logger.error('Failed to load offer for editing', { error: err, offerId: id });
      } finally {
        setLoading(false);
      }
    };

    fetchOffer();
  }, [id]);

  const handleChange = useCallback(<K extends keyof UpdateOfferRequest>(
    key: K,
    value: UpdateOfferRequest[K]
  ) => {
    setFormData(prev => prev ? { ...prev, [key]: value } : null);
  }, []);

  const handleContextChange = useCallback((context: OfferContext | null) => {
    setFormData(prev => prev ? { ...prev, context_json: context } : null);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !formData) return;

    setSaving(true);
    setError(null);

    try {
      await offerService.updateOffer(id, formData);
      
      // Log record update for audit
      auditService.logRecordModification('updated', 'offer', id, {
        assignment_strategy: formData.assignment_strategy,
        display_priority: formData.display_priority,
      }).catch((err) => logger.warn('Failed to log record update', { error: err }));
      
      invalidate();
      refresh(); // Force immediate refetch
      navigate(`/offers/${id}`);
    } catch (err) {
      setError('Failed to save changes');
      logger.error('Failed to save offer changes', { error: err, offerId: id });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="offer-edit-page">
        <div className="loading">Loading offer...</div>
      </div>
    );
  }

  if (error && !formData) {
    return (
      <div className="offer-edit-page">
        <div className="error-state">
          <p>{error}</p>
          <button onClick={() => navigate('/offers')}>Back to Offers</button>
        </div>
      </div>
    );
  }

  if (!formData || !originalOffer) {
    return null;
  }

  const strategyOptions = ASSIGNMENT_STRATEGY_OPTIONS.map(opt => ({
    value: opt.value,
    label: opt.label,
  }));

  return (
    <div className="offer-edit-page">
      <div className="back-nav">
        <button onClick={() => navigate(`/offers/${id}`)} className="btn-back">
          ← Back to Offer Details
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit}>
        {/* Read-only info */}
        <Card>
          <CardHeader title="Offer Information (Read-Only)" />
          <CardBody>
            <div className="readonly-grid">
              <div className="readonly-field">
                <label>Offer ID</label>
                <span className="mono">{originalOffer.offer_id}</span>
              </div>
              <div className="readonly-field">
                <label>User ID</label>
                <span className="mono">{originalOffer.user_id}</span>
              </div>
              <div className="readonly-field">
                <label>Reward</label>
                <span>{originalOffer.reward_name}</span>
              </div>
              <div className="readonly-field">
                <label>Partner</label>
                <span>{originalOffer.partner_name}</span>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Editable fields */}
        <Card>
          <CardHeader title="Editable Fields" />
          <CardBody>
            <div className="form-grid">
              <Select
                label="Assignment Strategy"
                value={formData.assignment_strategy || ''}
                onChange={(val) => handleChange('assignment_strategy', val as AssignmentStrategy)}
                options={strategyOptions}
              />
              <Input
                label="Display Priority"
                type="number"
                value={(formData.display_priority || 1).toString()}
                onChange={(val) => handleChange('display_priority', parseInt(val, 10) || 1)}
              />
            </div>
            {/* Show priority validation - exclude current offer from conflicts */}
            {originalOffer && (
              <PriorityValidation
                userId={originalOffer.user_id}
                partnerProductId={originalOffer.partner_product_id || 0}
                currentPriority={formData.display_priority || 1}
                excludeOfferId={originalOffer.offer_id}
                onPriorityChange={(priority) => handleChange('display_priority', priority)}
              />
            )}
          </CardBody>
        </Card>

        {/* Context Editor */}
        <Card>
          <CardHeader title="Context Data" />
          <CardBody>
            <OfferContextEditor
              value={formData.context_json ?? null}
              strategy={formData.assignment_strategy || originalOffer.assignment_strategy}
              onChange={handleContextChange}
            />
          </CardBody>
        </Card>

        {/* Actions */}
        <div className="form-actions">
          <button
            type="button"
            className="btn-cancel"
            onClick={() => navigate(`/offers/${id}`)}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-save"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default OfferEdit;
